import React, { useState, createContext, useContext } from 'react';
import MuiMenu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';

interface MenuContextValue {
  mode?: 'horizontal' | 'vertical' | 'inline';
  closeSubmenu?: () => void;
}

const MenuCtx = createContext<MenuContextValue>({});

export interface MenuProps {
  mode?: 'horizontal' | 'vertical' | 'inline';
  selectedKeys?: string[];
  defaultSelectedKeys?: string[];
  openKeys?: string[];
  style?: React.CSSProperties;
  className?: string;
  children?: React.ReactNode;
  onClick?: (info: { key: string; keyPath: string[] }) => void;
  theme?: 'light' | 'dark';
  [key: string]: any;
}

export interface MenuItemProps {
  key?: React.Key;
  eventKey?: React.Key;
  children?: React.ReactNode;
  style?: React.CSSProperties;
  className?: string;
  disabled?: boolean;
  onClick?: React.MouseEventHandler;
  icon?: React.ReactNode;
  [key: string]: any;
}

export interface SubMenuProps {
  key?: React.Key;
  title?: React.ReactNode;
  children?: React.ReactNode;
  popupClassName?: string;
  className?: string;
  [key: string]: any;
}

const MenuItemComp: React.FC<MenuItemProps> = ({ children, style, className, disabled, onClick, icon }) => {
  const { mode } = useContext(MenuCtx);
  if (mode === 'horizontal') {
    return (
      <Box
        component="span"
        className={className}
        sx={{ display: 'inline-flex', alignItems: 'center', cursor: disabled ? 'default' : 'pointer', ...style }}
        onClick={disabled ? undefined : onClick}
      >
        {icon}
        {children}
      </Box>
    );
  }
  return (
    <MenuItem disabled={disabled} className={className} sx={style} onClick={onClick as any}>
      {icon}
      {children}
    </MenuItem>
  );
};

const SubMenu: React.FC<SubMenuProps> = ({ title, children, className, popupClassName }) => {
  const { mode } = useContext(MenuCtx);
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const open = Boolean(anchor);

  if (mode === 'horizontal') {
    return (
      <>
        <Button
          className={className}
          onClick={(e) => setAnchor(e.currentTarget)}
          sx={{ textTransform: 'none', color: 'inherit', minWidth: 0 }}
        >
          {title}
        </Button>
        <MuiMenu
          anchorEl={anchor}
          open={open}
          onClose={() => setAnchor(null)}
          className={popupClassName}
        >
          {React.Children.map(children, (child) => {
            if (!React.isValidElement(child)) return child;
            return React.cloneElement(child as React.ReactElement<any>, {
              onClick: (e: React.MouseEvent) => {
                child.props.onClick?.(e);
                setAnchor(null);
              },
            });
          })}
        </MuiMenu>
      </>
    );
  }

  return (
    <Box className={className}>
      <Box sx={{ fontWeight: 600, px: 2, py: 1 }}>{title}</Box>
      <Box sx={{ pl: 1 }}>{children}</Box>
    </Box>
  );
};

const MenuDivider: React.FC = () => {
  const { mode } = useContext(MenuCtx);
  return mode === 'horizontal' ? null : <Divider />;
};

const MenuRoot: React.FC<MenuProps> = ({
  mode = 'vertical',
  style,
  className,
  children,
  onClick,
}) => {
  const horizontal = mode === 'horizontal';
  return (
    <MenuCtx.Provider value={{ mode }}>
      <Box
        className={className}
        component={horizontal ? 'div' : 'ul'}
        sx={{
          display: horizontal ? 'flex' : 'block',
          flexDirection: horizontal ? 'row' : 'column',
          alignItems: horizontal ? 'center' : 'stretch',
          listStyle: 'none',
          m: 0,
          p: horizontal ? 0 : 1,
          ...style,
        }}
        role="menu"
      >
        {React.Children.map(children, (child) => {
          if (!React.isValidElement(child)) return child;
          const key = child.key != null ? String(child.key) : child.props.eventKey;
          return React.cloneElement(child as React.ReactElement<any>, {
            onClick: (e: React.MouseEvent) => {
              child.props.onClick?.(e);
              if (key) onClick?.({ key, keyPath: [key] });
            },
          });
        })}
      </Box>
    </MenuCtx.Provider>
  );
};

export type MenuComponent = typeof MenuRoot & {
  Item: typeof MenuItemComp;
  SubMenu: typeof SubMenu;
  Divider: typeof MenuDivider;
};

export const Menu = MenuRoot as MenuComponent;
Menu.Item = MenuItemComp;
Menu.SubMenu = SubMenu;
Menu.Divider = MenuDivider;

export default Menu;
